/**
 * Tải + cache texture vật liệu cho viewer (work-order 04d).
 *
 * Cache theo URL: nhiều part cùng vật liệu **chia sẻ `.source`** (ảnh upload GPU 1 lần — quan
 * trọng cho VRAM mobile), nhưng mỗi part nhận **1 clone riêng** để đặt `repeat`/`rotation` độc
 * lập — vì những thuộc tính này nằm trên chính object `Texture`, không phải material. Dùng chung
 * 1 Texture cho nhiều tấm khác kích thước/hướng sẽ ghi đè lẫn nhau (xem 04d §điểm 1).
 *
 * Tải bất đồng bộ (progressive): scene hiện màu trơn trước, swap sang texture khi ảnh tải xong.
 * Lỗi tải → throw để caller giữ màu trơn.
 */
import {
  ClampToEdgeWrapping,
  MirroredRepeatWrapping,
  RepeatWrapping,
  SRGBColorSpace,
  Texture,
  TextureLoader,
  type WebGLRenderer,
} from 'three';
import type { TextureOrientation } from '../lib/types/wvm';

/** Tham số tiling + hướng vân để cấu hình texture cho 1 part box. */
export interface TextureSpec {
  url: string;
  /** kích thước thật 1 lần lặp ảnh (mm) = stepMm. */
  repeatMmU: number;
  repeatMmV: number;
  /** kích thước mặt chính của tấm (mm) — để tính mật độ lặp theo mm thật. */
  faceWidthMm: number;
  faceHeightMm: number;
  /** hướng vân per-part (04d) — dùng khi `materialOrientation` null. */
  orientation: TextureOrientation;
  // --- tham số per-model 04e (default = trung tính → hành vi 04d) ---
  /** dịch gốc vân (mm). */
  shiftMmU?: number;
  shiftMmV?: number;
  /** xoay thêm tinh (độ), cộng dồn orientation thô. */
  angleDeg?: number;
  /** {0,90,180,270} ép material-level; null/undefined = kế thừa `orientation` per-part. */
  materialOrientation?: number | null;
  mirrored?: boolean;
  /** kéo 1 ảnh phủ kín mặt (bỏ tiling/shift). */
  stretch?: boolean;
  /** 0..100 — no-op v1 (giữ anisotropy=max như 04d; xem 04e §2). Nhận để parity, không áp. */
  sharpness?: number;
  /**
   * UV đã ở ĐƠN VỊ mm (mesh-inline 04f, plugin xuất `uvs` mm) thay vì [0,1] như box/extrusion.
   * → tiling = `repeat 1/step` (uv_mm × 1/step = số tile), KHÔNG dùng faceWidth/Height; xoay quanh gốc.
   */
  uvSpaceMm?: boolean;
}

export class TextureManager {
  private readonly loader = new TextureLoader();
  /**
   * Cache base theo URL — lưu **Promise** (không phải Texture đã resolve): nhiều part cùng URL
   * gọi `acquire()` ĐỒNG THỜI (applyTextures không await) đều **chờ chung 1 lần tải** → mọi clone
   * chia sẻ một `.source` (1 ảnh GPU). Nếu cache Texture-đã-resolve sẽ bị race: các part chạy
   * trước khi tải xong đều thấy cache rỗng → tải trùng → mất chia sẻ (rò VRAM).
   */
  private readonly base = new Map<string, Promise<Texture>>();
  /** mọi texture đã tạo (gốc + clone) — dispose toàn bộ khi đổi/đóng model. */
  private readonly all = new Set<Texture>();
  private readonly maxAniso: number;

  constructor(renderer: WebGLRenderer) {
    // mặc định của Three; an toàn (và bắt buộc) cho kịch bản prod cross-origin.
    this.loader.setCrossOrigin('anonymous');
    this.maxAniso = renderer.capabilities.getMaxAnisotropy();
  }

  /**
   * Trả về 1 Texture đã cấu hình tiling/hướng vân cho 1 part. Tải ảnh (cache Promise theo URL)
   * rồi clone để áp thông số riêng. Reject nếu tải ảnh lỗi (caller giữ màu trơn).
   */
  async acquire(spec: TextureSpec): Promise<Texture> {
    let baseP = this.base.get(spec.url);
    if (!baseP) {
      baseP = this.loadBase(spec.url);
      this.base.set(spec.url, baseP);
    }
    const baseTex = await baseP;
    const tex = baseTex.clone();
    tex.needsUpdate = true; // bắt buộc sau clone để áp `.source`
    this.applyTiling(tex, spec);
    this.all.add(tex);
    return tex;
  }

  /** Tải + cấu hình base texture cho 1 URL (gọi đúng 1 lần/URL nhờ cache Promise). */
  private async loadBase(url: string): Promise<Texture> {
    const t = await this.loader.loadAsync(url);
    t.colorSpace = SRGBColorSpace;
    t.wrapS = t.wrapT = RepeatWrapping;
    t.anisotropy = this.maxAniso;
    this.all.add(t);
    return t;
  }

  /**
   * Tiling theo mm thật + xoay (orientation thô + angle tinh) + shift/mirror/stretch (04e).
   * Mọi tham số ở default (shift 0, angle 0, materialOrientation null, mirror/stretch false) →
   * cho ra ĐÚNG kết quả 04d (không hồi quy — xem 04e §5).
   */
  private applyTiling(tex: Texture, spec: TextureSpec): void {
    const stepU = spec.repeatMmU > 0 ? spec.repeatMmU : 1;
    const stepV = spec.repeatMmV > 0 ? spec.repeatMmV : 1;
    // mật độ lặp = kích thước tấm / kích thước 1 lần lặp ảnh (giữ mật độ vân đều mọi kích thước).
    const repU = spec.faceWidthMm / stepU;
    const repV = spec.faceHeightMm / stepV;

    // orientation THÔ (độ) = per-part (HÌNH HỌC: horizontal=90 / vertical|none=0 — Cách A-v4 cho vân
    // dọc cạnh dài từng tấm) CỘNG material-level {0,90,180,270} (hiệu chỉnh ẢNH vật liệu). material
    // ADDITIVE (KHÔNG ghi đè): để xoay vân TOÀN CỤC (vd ảnh vật liệu có chiều vân xoay 90°) mà VẪN giữ
    // hướng per-part đúng từng tấm. Auto (null) = +0. (Trước đây ép/override → mất per-part → Бок sai.)
    const perPartDeg = spec.orientation === 'horizontal' ? 90 : 0;
    const coarseDeg = (perPartDeg + (spec.materialOrientation ?? 0)) % 360;
    // hoán U↔V khi thành phần THÔ là 90°/270° (giữ mật độ như 04d horizontal); KHÔNG theo angle tinh.
    const swap = ((coarseDeg % 180) + 180) % 180 === 90;

    // UV đã ở mm (mesh-inline 04f): repeat = 1/step (uv_mm × repeat = số tile), KHÔNG dùng faceSize.
    // stretch không áp dụng (cần [0,1]); xoay quanh gốc vì uv không chuẩn hoá. shift/mirror/orientation vẫn áp.
    if (spec.uvSpaceMm) {
      tex.wrapS = tex.wrapT = spec.mirrored ? MirroredRepeatWrapping : RepeatWrapping;
      tex.center.set(0, 0);
      tex.rotation = ((coarseDeg + (spec.angleDeg ?? 0)) * Math.PI) / 180;
      const ru = 1 / stepU;
      const rv = 1 / stepV;
      if (swap) tex.repeat.set(rv, ru);
      else tex.repeat.set(ru, rv);
      tex.offset.set((spec.shiftMmU ?? 0) / stepU, (spec.shiftMmV ?? 0) / stepV);
      return;
    }

    // wrap: stretch (clamp) thắng mirror (gương) thắng repeat thường.
    tex.wrapS = tex.wrapT = spec.stretch
      ? ClampToEdgeWrapping
      : spec.mirrored
        ? MirroredRepeatWrapping
        : RepeatWrapping;

    tex.center.set(0.5, 0.5);
    tex.rotation = ((coarseDeg + (spec.angleDeg ?? 0)) * Math.PI) / 180;

    if (spec.stretch) {
      // 1 ảnh phủ kín mặt → bỏ tiling + shift (vẫn giữ xoay).
      tex.repeat.set(1, 1);
      tex.offset.set(0, 0);
      return;
    }

    if (swap) tex.repeat.set(repV, repU);
    else tex.repeat.set(repU, repV);
    // shift (mm → đơn vị lặp) theo trục ảnh U/V.
    tex.offset.set((spec.shiftMmU ?? 0) / stepU, (spec.shiftMmV ?? 0) / stepV);
  }

  /** Dispose toàn bộ texture (gốc + clone) + reset cache. Gọi khi đổi/đóng model. */
  clear(): void {
    for (const t of this.all) t.dispose();
    this.all.clear();
    this.base.clear();
  }
}
